import { Store } from '../state/store';
import { renderMessage, renderMessageSync, clearCache } from '../rendering';
import { renderActionHistory, setupActionHistoryToggle } from './action-history';
import { renderReactionBubble } from './reaction-picker';
import type { Message, StreamingState } from '../state/types';
import { Component } from './base';

export class MessageListComponent extends Component {
    private store: Store;
    private timestampUpdater: number | null = null;
    private currentRenderId: number = 0;

    constructor(element: HTMLElement, store: Store) {
        super(element);
        this.store = store;
        setupActionHistoryToggle(element);
        this.setupMessageListEvents();
        this.subscribeToStore();
        this.render();
        this.startTimestampUpdater();
    }

    private subscribeToStore(): void {
        this.unsubscribers.push(this.store.subscribeToMessages(() => this.onMessagesChange()));
        this.unsubscribers.push(this.store.subscribeToCurrentConversationId(() => this.onConversationChange()));
        this.unsubscribers.push(this.store.subscribeToMessageListVersion(() => this.onMessageListVersionChange()));
        this.unsubscribers.push(this.store.subscribeToStreamingStates(() => this.onStreamingStateChange()));
    }

    destroy(): void {
        super.destroy();
        if (this.timestampUpdater) {
            clearInterval(this.timestampUpdater);
        }
    }

    private startTimestampUpdater(): void {
        this.timestampUpdater = window.setInterval(() => {
            this.updateTimestamps();
        }, 60000);
    }

    private updateTimestamps(): void {
        this.element.querySelectorAll('.message-timestamp').forEach(el => {
            const timestampEl = el as HTMLElement;
            const time = timestampEl.getAttribute('data-time');
            if (time) {
                timestampEl.textContent = this.formatRelativeTime(time);
            }
        });
    }

    protected render(): void {
        this.fullReRender();
    }

    private onMessagesChange(): void {
        const messages = this.store.getVisibleMessages();
        const messagesList = this.element.querySelector('#messages-list');
        if (!messagesList) return;

        const currentElements = messagesList.querySelectorAll('.message');
        const currentCount = currentElements.length;
        const newCount = messages.length;

        if (newCount > currentCount) {
            if (currentCount === 0) {
                messagesList.innerHTML = '';
            }
            const newMessages = messages.slice(currentCount);
            this.appendMessages(newMessages);
        } else if (newCount < currentCount) {
            this.fullReRender();
        } else if (newCount === currentCount && newCount > 0) {
            const lastMsg = messages[messages.length - 1];
            const lastEl = currentElements[currentCount - 1] as HTMLElement;
            if (lastEl && lastEl.getAttribute('data-id') !== lastMsg.id) {
                this.fullReRender();
            }
        }
    }

    private onConversationChange(): void {
        this.fullReRender();
    }

    private onMessageListVersionChange(): void {
        this.fullReRender();
    }

    private onStreamingStateChange(): void {
        const conversationId = this.store.getCurrentConversationId();
        const streamingState = this.store.getStreamingState(conversationId);
        this.updateCursor(streamingState);
    }

    private setupMessageListEvents(): void {
        this.element.addEventListener('click', (e) => {
            const copyBtn = (e.target as HTMLElement).closest('.message-copy-btn');
            if (copyBtn) {
                this.dispatch('copy-message', copyBtn.getAttribute('data-id'));
                return;
            }

            const codeCopyBtn = (e.target as HTMLElement).closest('.code-copy-btn');
            if (codeCopyBtn) {
                this.handleCodeCopy(codeCopyBtn as HTMLButtonElement);
                return;
            }

            const editBtn = (e.target as HTMLElement).closest('.message-edit-btn');
            if (editBtn) {
                this.dispatch('edit-message', editBtn.getAttribute('data-id'));
                return;
            }

            const tryAgainBtn = (e.target as HTMLElement).closest('.message-try-again-btn');
            if (tryAgainBtn) {
                this.dispatch('try-again', tryAgainBtn.getAttribute('data-id'));
                return;
            }

            const reactionTrigger = (e.target as HTMLElement).closest('.reaction-trigger');
            if (reactionTrigger) {
                const rect = (reactionTrigger as HTMLElement).getBoundingClientRect();
                this.dispatch('add-reaction', {
                    messageId: reactionTrigger.getAttribute('data-message-id'),
                    x: rect.left,
                    y: rect.bottom + 8
                });
                return;
            }

            const img = (e.target as HTMLElement).closest('.message-image');
            if (img) {
                const imageIndex = parseInt(img.getAttribute('data-image-index') || '0');
                const totalImagesInMessage = parseInt(img.getAttribute('data-total-images') || '1');
                this.dispatch('show-lightbox', { imageIndex, totalImagesInMessage });
                return;
            }

            const message = (e.target as HTMLElement).closest('.message');
            if (message && message.classList.contains('message-assistant')) {
                if (!(e.target as HTMLElement).closest('.message-controls')) {
                    this.element.querySelectorAll('.message.controls-visible').forEach(el => {
                        el.classList.remove('controls-visible');
                    });
                    message.classList.toggle('controls-visible');
                }
            }
        });
    }

    private async handleCodeCopy(button: HTMLButtonElement): Promise<void> {
        const code = button.getAttribute('data-code');
        if (!code) return;

        try {
            await navigator.clipboard.writeText(code);
            button.classList.add('copied');
            button.setAttribute('data-tooltip', 'Copied!');
            
            setTimeout(() => {
                button.classList.remove('copied');
                button.setAttribute('data-tooltip', 'Copy');
            }, 2000);
        } catch (err) {
            console.error('Failed to copy code:', err);
        }
    }

    private async fullReRender(): Promise<void> {
        this.currentRenderId++;

        const renderId = this.currentRenderId;
        const messages = this.store.getVisibleMessages();

        const messagesList = this.element.querySelector('#messages-list');
        if (!messagesList) return;

        const isLoading = this.store.isLoadingMessagesValue();
        
        if (messages.length === 0 && !isLoading) {
            const conversationId = this.store.getCurrentConversationId();
            const conversations = this.store.getConversations();
            const conversation = conversations.find(c => c.id === conversationId);
            const isNewSession = !conversation?.title;
            
            if (isNewSession) {
                messagesList.innerHTML = `
                    <div class="new-session-indicator">
                        <div class="new-session-icon">
                            <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                        </div>
                        <div class="new-session-title">New conversation</div>
                        <div class="new-session-hint">Type a message to get started</div>
                    </div>
                `;
            } else {
                messagesList.innerHTML = '';
            }
            return;
        }

        const messageElements = await this.renderMessageElements(messages);

        if (renderId !== this.currentRenderId) {
            return;
        }

        messagesList.innerHTML = '';
        messagesList.append(...messageElements);

        if (renderId !== this.currentRenderId) {
            return;
        }

        (window as any).lucide?.createIcons();

        const conversationId = this.store.getCurrentConversationId();
        const streamingState = this.store.getStreamingState(conversationId);
        this.updateCursor(streamingState);
    }

    private async appendMessages(messages: Message[]): Promise<void> {
        this.currentRenderId++;
        const renderId = this.currentRenderId;

        const messagesList = this.element.querySelector('#messages-list');
        if (!messagesList) {
            return;
        }

        const newElements = await this.renderMessageElements(messages);

        if (renderId !== this.currentRenderId) {
            return;
        }

        messagesList.append(...newElements);
        (window as any).lucide?.createIcons();

        const conversationId = this.store.getCurrentConversationId();
        const streamingState = this.store.getStreamingState(conversationId);
        this.updateCursor(streamingState);
    }

    private updateCursor(streamingState: StreamingState): void {
        const messages = this.element.querySelectorAll('.message');
        const lastMsg = messages[messages.length - 1];
        if (!lastMsg) return;

        const shouldShowCursor = this.shouldShowCursor(lastMsg, streamingState);

        if (shouldShowCursor && !lastMsg.querySelector('.thinking-indicator')) {
            const content = lastMsg.querySelector('.message-content');
            content?.insertAdjacentHTML('beforeend', '<span class="thinking-indicator"><span class="ellipsis-dot"></span><span class="ellipsis-dot"></span><span class="ellipsis-dot"></span></span>');
        } else if (!shouldShowCursor) {
            lastMsg.querySelector('.thinking-indicator')?.remove();
        }
    }

    private shouldShowCursor(lastMsg: Element, streamingState: StreamingState): boolean {
        const isAssistant = lastMsg.getAttribute('class')?.includes('message-assistant')!;
        return isAssistant && streamingState.isStreaming && !streamingState.hasReceivedFirstToken;
    }

    private async renderSingleMessage(message: Message, imageIndex: number): Promise<HTMLTemplateElement> {
        const template = document.createElement('template');
        const renderedContent = await renderMessage(message.content);

        const messageImageIndex = imageIndex;
        const imageCount = message.images?.length || 0;
        const isError = this.isError(message);
        
        const actionHistoryHtml = message.role === 'assistant' && message.actionSteps 
            ? renderActionHistory(message.actionSteps) 
            : '';
        
        const reactionBubblesHtml = renderReactionBubble(message.reactions, message.role);
        
        const hasDisregardAck = message.flags?.includes('disregard_ack');
        
        const reactionTriggerHtml = message.role === 'assistant' ? `
            <button class="reaction-trigger" data-tooltip="React" data-message-id="${message.id}">
                <i data-lucide="smile"></i>
            </button>
        ` : '';

        let imagesHtml = '';
        if (message.images && message.images.length > 0) {
            imagesHtml = `
                <div class="message-images">
                    ${message.images.map((img, imgIdx) => `
                        <img src="data:${img.media_type};base64,${img.data}" alt="Attached image ${imgIdx + 1}"
                             class="message-image"
                             data-image-index="${messageImageIndex + imgIdx}"
                             data-total-images="${imageCount}">
                    `).join('')}
                </div>
            `;
        }

        const disregardHtml = hasDisregardAck ? '<span class="disregard-indicator"><i data-lucide="circle-check-big"></i></span>' : '';
        
        const messageHTML = `
            <div class="message ${isError ? 'message-error' : `message-${message.role}`}" data-id="${message.id}" ${message.role === 'assistant' ? 'data-can-react="true"' : ''}>
                <div class="message-wrapper">
                    ${imagesHtml}
                    ${actionHistoryHtml}
                    <div class="message-content">${renderedContent}</div>
                    <div class="message-footer">
                        ${message.role === 'assistant' ? reactionBubblesHtml : ''}
                        ${message.role === 'assistant' ? disregardHtml : ''}
                        <div class="message-controls" data-id="${message.id}" data-raw="${this.escapeHtml(message.content)}">
                            ${reactionTriggerHtml}
                            <button class="message-copy-btn" data-tooltip="Copy" data-id="${message.id}">
                                <i data-lucide="copy"></i>
                            </button>
                            ${message.role === 'user' ? `<button class="message-edit-btn" data-tooltip="Edit" data-id="${message.id}">
                                <i data-lucide="edit-3"></i>
                            </button>` : `<button class="message-try-again-btn" data-tooltip="Try again" data-id="${message.id}">
                                <i data-lucide="refresh-cw"></i>
                            </button>`}
                            <span class="message-timestamp" data-time="${message.created_at}">${this.formatRelativeTime(message.created_at)}</span>
                        </div>
                        ${message.role === 'user' ? disregardHtml : ''}
                        ${message.role === 'user' ? reactionBubblesHtml : ''}
                    </div>
                </div>
            </div>
        `;

        template.innerHTML = messageHTML;
        return template;
    }

    private async renderMessageElements(messages: Message[]): Promise<DocumentFragment[]> {
        let imageIndex = 0;
        const fragments: DocumentFragment[] = [];

        for (const message of messages) {
            const template = await this.renderSingleMessage(message, imageIndex);
            const fragment = template.content.cloneNode(true) as DocumentFragment;
            fragments.push(fragment);

            const imageCount = message.images?.length || 0;
            if (imageCount > 0) {
                imageIndex += imageCount;
            }
        }

        return fragments;
    }

    private isError(message: Message): boolean {
        return message.role === 'assistant' && message.content.startsWith('Error:');
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    private formatRelativeTime(timestamp: string): string {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffSecs < 60) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString();
    }

    public updateLastMessage(content: string, messageId?: string): void {
        const selector = messageId ? `.message[data-id="${messageId}"]` : '.message:last-child.message-assistant';

        const controls = this.element.querySelectorAll(`${selector} .message-controls`);
        controls.forEach(ctrl => {
            const element = ctrl as HTMLElement;
            const rawAttr = element.getAttribute('data-raw');
            if (rawAttr) {
                element.setAttribute('data-raw', this.escapeHtml(content));
            }
        });

        const messageContents = this.element.querySelectorAll(`${selector} .message-content`);
        messageContents.forEach(msg => {
            const element = msg as HTMLElement;
            element.innerHTML = renderMessageSync(content);
        });

        (window as any).lucide?.createIcons();
    }

    public updateActionHistory(messageId: string, actionSteps: any[]): void {
        const messageEl = this.element.querySelector(`.message[data-id="${messageId}"]`);
        if (!messageEl) return;

        const existingHistory = messageEl.querySelector('.action-history');
        const wrapper = messageEl.querySelector('.message-wrapper');
        if (!wrapper) return;

        const wasExpanded = existingHistory?.getAttribute('data-expanded') === 'true';
        const newHistoryHtml = renderActionHistory(actionSteps);
        
        if (existingHistory && newHistoryHtml) {
            const template = document.createElement('template');
            template.innerHTML = newHistoryHtml;
            const newHistoryEl = template.content.firstElementChild as HTMLElement;
            if (wasExpanded) {
                newHistoryEl.setAttribute('data-expanded', 'true');
            }
            existingHistory.replaceWith(newHistoryEl);
        } else if (newHistoryHtml) {
            const messageContent = wrapper.querySelector('.message-content');
            if (messageContent) {
                messageContent.insertAdjacentHTML('beforebegin', newHistoryHtml);
            }
        }
    }

    public removeCursor(): void {
        this.element.querySelectorAll('.thinking-indicator').forEach(cursor => {
            cursor.remove();
        });
    }

    public getAllImages(): string[] {
        const images: string[] = [];
        this.store.getMessages().forEach(msg => {
            if (msg.images) {
                msg.images.forEach(img => {
                    images.push(`data:${img.media_type};base64,${img.data}`);
                });
            }
        });
        return images;
    }

    public showControlsOnLastMessage(): void {
        const messages = this.element.querySelectorAll('.message');
        if (messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.classList.contains('message-assistant')) {
            lastMessage.classList.add('controls-visible');
        }
    }

    public clearVisibleControls(): void {
        this.element.querySelectorAll('.message.controls-visible').forEach(msg => {
            msg.classList.remove('controls-visible');
        });
    }

    private dispatch(type: string, detail?: any): void {
        this.element.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
    }
}